import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { Form, Formik, Field } from 'formik';
import * as Yup from 'yup';

import { ICreateLinkModuleInput, IOpenOffsetType } from 'graphql/types';
import { Button } from 'common/components/Button';
import { TextField, LinkField } from 'common/components/FormField';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import { ContentExpander } from 'common/components/ContentExpander';
import { Typography } from 'common/components/Typography';
import { IModuleFormValues } from 'training/types';
import { ModuleImageFormSection } from 'training/components/ModuleImageFormSection';
import { ModuleOffsetFormSection } from 'training/components/ModuleOffsetFormSection';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';

export interface ILinkModuleFormValues
    extends
        IModuleFormValues,
        Omit<ICreateLinkModuleInput, 'imageId' | 'offset'> {}

export interface IProps {
    initialValues?: Partial<ILinkModuleFormValues>;
    disabled?: boolean;
    edit?: boolean;
    onClose?(): void;
    onSubmit(values: ILinkModuleFormValues): void;
    onDelete?(): void;
    moduleGroupId?: string;
}

export const LinkModuleForm = ({
    initialValues,
    disabled,
    edit,
    moduleGroupId,
    onClose,
    onDelete,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const [settingsExpanded, setSettingsExpanded] = useState(false);

    const validationSchema = Yup.object().shape({
        title: Yup.string().required(translate('validate.required')),
        url: Yup.string().required(translate('validate.required')),
    });

    const initValues: ILinkModuleFormValues = {
        title: '',
        url: '',
        offset: {
            type: IOpenOffsetType.Zero,
            days: 0,
            hours: 0,
        },
        ...initialValues,
    };

    const formTitle = edit
        ? translate('moduleForm.link.editTitle')
        : translate('moduleForm.link.newTitle');
    const formText = edit
        ? translate('moduleForm.link.editText')
        : translate('moduleForm.link.newText');
    const submitText = edit
        ? translate('moduleForm.link.editButton')
        : translate('moduleForm.link.newButton');

    return (
        <Formik
            initialValues={initValues}
            validationSchema={validationSchema}
            {...other}
        >
            {({ submitForm, isSubmitting, dirty, values }) => (
                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={dirty && !isSubmitting}
                        onConfirm={submitForm}
                    />
                    <Form>
                        {formTitle && formText && (
                            <Box sx={{ mb: 4 }}>
                                {formTitle && (
                                    <Typography
                                        sx={{ fontWeight: 'bold' }}
                                        variant="h3"
                                    >
                                        {formTitle}
                                    </Typography>
                                )}

                                {formText && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography>{formText}</Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        <LinkField name="url" />

                        <Field
                            component={TextField}
                            label={translate('title')}
                            name="title"
                        />

                        <Field
                            multiline
                            component={TextField}
                            label={translate('description')}
                            name="description"
                        />

                        <Box sx={{ mt: 4 }}>
                            <ContentExpander
                                expand={settingsExpanded}
                                title={translate('moreSettings')}
                                onClick={() =>
                                    setSettingsExpanded(!settingsExpanded)
                                }
                            >
                                <ModuleImageFormSection />
                                {/* Only show conditions section when we have a module group id */}
                                {moduleGroupId && (
                                    <ModuleOffsetFormSection
                                        moduleGroupId={moduleGroupId}
                                        offsetValues={
                                            values.offset || undefined
                                        }
                                    />
                                )}
                            </ContentExpander>
                        </Box>

                        <Box sx={{ display: 'flex', mt: 4 }}>
                            <Box>
                                <Button
                                    color="primary"
                                    disabled={
                                        isSubmitting ||
                                        disabled ||
                                        (!isSubmitting && !disabled && !dirty)
                                    }
                                    loading={isSubmitting}
                                    type="submit"
                                    variant="contained"
                                    onClick={(
                                        e: React.MouseEvent<HTMLButtonElement>
                                    ) => {
                                        e.preventDefault();

                                        return submitForm();
                                    }}
                                >
                                    {submitText}
                                </Button>
                            </Box>
                            {onClose && (
                                <Box sx={{ ml: 2 }}>
                                    <Button
                                        disabled={isSubmitting}
                                        variant="contained"
                                        onClick={onClose}
                                    >
                                        {translate('cancel')}
                                    </Button>
                                </Box>
                            )}
                            {onDelete && (
                                <Box sx={{ ml: 'auto' }}>
                                    <FormDeleteButton
                                        disabled={isSubmitting || disabled}
                                        onClick={onDelete}
                                    >
                                        {translate('delete')}
                                    </FormDeleteButton>
                                </Box>
                            )}
                        </Box>
                    </Form>
                </Box>
            )}
        </Formik>
    );
};
